import { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";

import { getReportRangeErrorMessage } from "src/utils/report/reportRange.util";

import { CardLayout, MTable } from "../v3";
import { clientReportColumns } from "../../pages/report/constants";
import { get_date } from "../generalComponets";
import { AnalyticsService } from "../../services";
import { downloadFile } from "../../utils/download.utils";

export const ClientReportResults = ({
  reportType,
  data = {},
  loading = false,
  paginationState,
  handlePaginationChange = () => {},
  reportFilters = {},
}) => {
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [pdfSuccess, setPdfSuccess] = useState(false);
  const [prevFilters, setPrevFilters] = useState({});

  useEffect(() => {
    if (pdfSuccess && JSON.stringify(reportFilters) !== JSON.stringify(prevFilters)) {
      setPdfSuccess(false);
    }
  }, [reportFilters, prevFilters, pdfSuccess]);

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    setPdfSuccess(false);
    try {
      const response = await AnalyticsService.downloadClientReportPdf(reportFilters || {});

      const currentDateTime = moment().format("YYYYMMDD-HHmmss");
      const filename = `client-report-${reportType}-${currentDateTime}.pdf`;

      downloadFile(response.data, filename);
      toast.success("Report downloaded successfully");

      setPdfSuccess(true);
      setPrevFilters(reportFilters);
    } catch (error) {
      console.error("Report download failed:", error);
      toast.error((await getReportRangeErrorMessage(error)) ?? "Failed to download report");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const rows =
    data?.rows?.map((row, index) => ({
      id: index + 1 + paginationState.page * paginationState.pageSize,
      sl: index + 1 + paginationState.page * paginationState.pageSize,
      formattedDate: get_date(row.date),
      payment: row?.entryType === "clientReceivable" ? row.amount : "-",
      income: row?.entryType === "income" ? row.amount : "-",
      ...row,
    })) || [];

  const totalOpeningBalance = rows.reduce((sum, row) => sum + Number(row.openingBalance || 0), 0);
  const totalReceivables = rows.reduce((sum, row) => sum + Number(row.totalReceivables || 0), 0);
  const totalIncomes = rows.reduce((sum, row) => sum + Number(row.totalIncomes || 0), 0);
  const totalClosingBalance = rows.reduce((sum, row) => sum + Number(row.closingBalance || 0), 0);
  const totalReceivableAmount = rows.reduce(
    (sum, row) => sum + (row.entryType === "clientReceivable" ? Number(row.amount || 0) : 0),
    0,
  );

  const totalIncomeAmount = rows.reduce((sum, row) => sum + (row.entryType === "income" ? Number(row.amount || 0) : 0), 0);

  const subtotal =
    data?.count > 0
      ? [
          {
            id: "subtotal",
            label: "Subtotal",
            totalOpeningBalance: totalOpeningBalance.toFixed(2),
            totalReceivables: totalReceivables.toFixed(2),
            totalIncomes: totalIncomes.toFixed(2),
            totalClosingBalance: totalClosingBalance.toFixed(2),
            totalReceivableAmount: totalReceivableAmount.toFixed(2),
            totalIncomeAmount: totalIncomeAmount.toFixed(2),
          },
        ]
      : [];

  const allRows = [...rows, ...subtotal];
  return (
    <CardLayout>
      <MTable
        // Export data
        showExportButton // TODO: implement permission
        isPdfGenerating={isPdfGenerating}
        pdfSuccess={pdfSuccess}
        currentFilters={reportFilters}
        prevFilters={prevFilters}
        onExportPdfClick={handleDownloadPdfClick}
        // Table data
        columnHeaders={clientReportColumns[reportType]}
        tableRows={allRows}
        rowCount={data?.count}
        loading={loading}
        paginationModel={paginationState}
        onPaginationChange={handlePaginationChange}
      />
    </CardLayout>
  );
};
